import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Box } from 'src/components/Box';
import { LinearProgress } from 'src/components/LinearProgress';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { useSession } from 'src/contexts/SessionContext';
import { FeatureFlag } from 'src/models/platform/_types';
import {
  fetchOnboarding,
  fetchOnboardingKey,
} from 'src/models/platform-onboarding/platform-onboarding';
import { isHeadTrainer } from 'src/models/user/utils';
import { hasFeatureFlag } from 'src/shared/usePermissionCheck';
import { calculateOnboardingProgress } from 'src/utils/onboardingProgress';

export const OnboardingProgress = () => {
  const { t: tCommon } = useTranslation('translation', { keyPrefix: 'common' });
  const navigate = useNavigate();
  const { user, session, sessionTrainer } = useSession();
  const platformId = session?.id;

  const { data: onboarding } = useQuery({
    queryKey: fetchOnboardingKey(platformId),
    queryFn: () => (platformId ? fetchOnboarding(platformId) : null),
    enabled: !!platformId && user && isHeadTrainer(user),
  });

  // Check if onboarding feature flag is set, if not, don't render
  if (
    !isHeadTrainer(user) ||
    !session ||
    !sessionTrainer ||
    !onboarding ||
    !hasFeatureFlag(session, FeatureFlag.Onboarding)
  ) {
    return null;
  }

  const { progressPercentage, completedSteps, totalSteps } =
    calculateOnboardingProgress(session, onboarding, sessionTrainer);

  // If onboarding is complete (100%), don't show the component
  if (progressPercentage === 100) {
    return null;
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box
        sx={{
          p: 2,
          cursor: 'pointer',
          borderRadius: 2,
          '&:hover': {
            bgcolor: 'action.hover',
          },
        }}
        onClick={() => navigate('/onboarding')}
      >
        <Stack spacing={1}>
          <Typography
            variant="subtitle2"
            color="text.primary"
            fontWeight="medium"
          >
            {tCommon('labels.onboardingProgress')}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={progressPercentage}
            color="success"
            sx={{
              height: 8,
              borderRadius: 1,
            }}
          />
          <Typography variant="caption" color="text.secondary" align="right">
            {tCommon('labels.completedSteps', { completedSteps, totalSteps })}
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
};
